import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Row, Col, Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSave, 
  faUser,
  faHome,
  faCalculator,
  faFileContract,
  faSignOutAlt,
  faCalendarAlt,
  faMoneyBillWave,
  faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { contractService } from '../../services/contractService';
import { tenantService } from '../../services/tenantService';
import { propertyService } from '../../services/propertyService';
import { formatCurrency, getCurrencySymbol } from '../../utils/currencyFormatter';

const CreateContract = () => {
  const navigate = useNavigate();
  const { userCompany, logout } = useAuth();
  
  const [tenants, setTenants] = useState([]);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [allPropertyUnits, setAllPropertyUnits] = useState([]);
  const [brokerageSettings, setBrokerageSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    tenantId: '',
    propertyUnitId: '',
    totalRentalAmount: '',
    numberOfCheques: '12',
    contractStartDate: '',
    contractEndDate: '',
    contractDuration: '',
    notes: ''
  });

  const [calculatedAmounts, setCalculatedAmounts] = useState({
    totalRentalAmount: 0,
    numberOfCheques: 12,
    chequeAmount: 0,
    brokeragePercentage: 5,
    brokerageFee: 0,
    totalAmount: 0,
    currency: 'USD'
  });

  useEffect(() => {
    fetchInitialData();
  }, [userCompany]);

  useEffect(() => {
    calculateAmounts();
  }, [formData.totalRentalAmount, formData.numberOfCheques, brokerageSettings]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      console.log('Fetching initial data for company:', userCompany);
      
      const [tenantsData, unitsData, settingsData] = await Promise.all([
        tenantService.getTenantsByCompany(userCompany),
        propertyService.getUnitsByCompany(userCompany),
        contractService.getBrokerageSettings(userCompany)
      ]);
      
      console.log('Fetched tenants:', tenantsData);
      console.log('Fetched units:', unitsData);
      console.log('Fetched settings:', settingsData);
      
      setTenants(tenantsData);
      setAllPropertyUnits(unitsData);
      console.log('All units statuses:', unitsData.map(unit => ({ id: unit.id, status: unit.status, unitNumber: unit.unitNumber })));
      // Filter for units that are available for contracts (not occupied)
      const availableUnits = unitsData.filter(unit => unit.status !== 'occupied');
      console.log('Available units:', availableUnits);
      setPropertyUnits(availableUnits);
      setBrokerageSettings(settingsData);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateAmounts = () => {
    const totalRental = parseFloat(formData.totalRentalAmount) || 0;
    const numberOfCheques = parseInt(formData.numberOfCheques) || 12;
    const brokeragePercentage = brokerageSettings?.brokeragePercentage || 5;
    const currency = brokerageSettings?.currency || 'USD';
    
    if (totalRental > 0 && numberOfCheques > 0) {
      const chequeAmount = totalRental / numberOfCheques;
      const brokerageFee = (totalRental * brokeragePercentage) / 100;
      const totalAmount = totalRental + brokerageFee;
      
      setCalculatedAmounts({
        totalRentalAmount: totalRental,
        numberOfCheques,
        chequeAmount: parseFloat(chequeAmount.toFixed(2)),
        brokeragePercentage,
        brokerageFee: parseFloat(brokerageFee.toFixed(2)),
        totalAmount: parseFloat(totalAmount.toFixed(2)),
        currency
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.tenantId || !formData.propertyUnitId || !formData.totalRentalAmount || 
        !formData.contractStartDate || !formData.contractEndDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseFloat(formData.totalRentalAmount) <= 0) {
      setError('Rental amount must be greater than 0');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const contractData = {
        tenantId: formData.tenantId,
        propertyUnitId: formData.propertyUnitId,
        totalRentalAmount: parseFloat(formData.totalRentalAmount),
        numberOfCheques: parseInt(formData.numberOfCheques),
        contractStartDate: formData.contractStartDate,
        contractEndDate: formData.contractEndDate,
        contractDuration: formData.contractDuration,
        notes: formData.notes,
        currency: brokerageSettings?.currency || 'USD',
        companyId: userCompany
      };

      const createdContract = await contractService.createContract(contractData);

      setSuccess('Contract created successfully!');
      
      // Update property unit status to occupied
      await propertyService.updatePropertyUnit(formData.propertyUnitId, {
        status: 'occupied'
      });
      
      // Redirect to contract details after a short delay
      setTimeout(() => {
        navigate(`/manager/contracts/${createdContract.id}`);
      }, 1500);

    } catch (error) {
      console.error('Error creating contract:', error);
      setError('Failed to create contract: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const getSelectedTenant = () => {
    return tenants.find(t => t.id === formData.tenantId);
  };

  const getSelectedProperty = () => {
    return propertyUnits.find(p => p.id === formData.propertyUnitId);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/dashboard')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Dashboard
          </Button>
          <div>
            <h2 className="mb-1">Create Property Contract</h2>
            <p className="text-muted mb-0">Create a new rental contract between tenant and property</p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>
            <FontAwesomeIcon icon={faHome} className="me-2" />
            Dashboard
          </Button>

        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={8}>
            {/* Contract Details */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faFileContract} className="text-primary me-2" />
                  Contract Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        Select Tenant <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="tenantId"
                        value={formData.tenantId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Choose a tenant...</option>
                        {tenants.map(tenant => (
                          <option key={tenant.id} value={tenant.id}>
                            {tenant.name} - {tenant.email}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faHome} className="me-1" />
                        Select Property Unit <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="propertyUnitId"
                        value={formData.propertyUnitId}
                        onChange={handleInputChange}
                        required
                        disabled={propertyUnits.length === 0}
                      >
                        <option value="">
                          {propertyUnits.length === 0 
                            ? 'No available property units found' 
                            : 'Choose a property unit...'
                          }
                        </option>
                        {propertyUnits.map(unit => (
                          <option key={unit.id} value={unit.id}>
                            {unit.unitNumber} - {unit.unitType} ({unit.bedrooms} bed, {unit.bathrooms} bath) - {unit.status || 'active'}
                          </option>
                        ))}
                      </Form.Select>
                      {propertyUnits.length === 0 && (
                        <div>
                          <Form.Text className="text-warning">
                            No available property units found. Please add property units first.
                          </Form.Text>
                          {allPropertyUnits.length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Found {allPropertyUnits.length} units but none are available for contracts.
                                <br />
                                Unit statuses: {allPropertyUnits.map(unit => `${unit.unitNumber}(${unit.status || 'active'})`).join(', ')}
                              </small>
                              <div className="mt-2">
                                <Button 
                                  variant="outline-warning" 
                                  size="sm"
                                  onClick={() => setPropertyUnits(allPropertyUnits)}
                                >
                                  Show All Units (Debug)
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        Contract Start Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="contractStartDate"
                        value={formData.contractStartDate}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        Contract End Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="contractEndDate"
                        value={formData.contractEndDate}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Contract Duration</Form.Label>
                  <Form.Control
                    type="text"
                    name="contractDuration"
                    value={formData.contractDuration}
                    onChange={handleInputChange}
                    placeholder="e.g., 12 months, 6 months"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional contract notes..."
                    rows="3"
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Financial Details */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="text-primary me-2" />
                  Financial Details
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                        Total Rental Amount <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="totalRentalAmount"
                        value={formData.totalRentalAmount}
                        onChange={handleInputChange}
                        placeholder={`Enter total rental amount (${getCurrencySymbol(brokerageSettings?.currency || 'USD')})`}
                        min="0"
                        step="0.01"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FontAwesomeIcon icon={faCreditCard} className="me-1" />
                        Number of Cheques <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="numberOfCheques"
                        value={formData.numberOfCheques}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="1">1 Cheque</option>
                        <option value="2">2 Cheques</option>
                        <option value="3">3 Cheques</option>
                        <option value="4">4 Cheques</option>
                        <option value="6">6 Cheques</option>
                        <option value="12">12 Cheques</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Selected Details */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">Selected Details</h5>
              </Card.Header>
              <Card.Body>
                {getSelectedTenant() && (
                  <div className="mb-3">
                    <h6>Tenant</h6>
                    <p className="mb-1"><strong>{getSelectedTenant().name}</strong></p>
                    <p className="mb-1 text-muted">{getSelectedTenant().email}</p>
                    <p className="mb-0 text-muted">{getSelectedTenant().phone}</p>
                  </div>
                )}

                {getSelectedProperty() && (
                  <div className="mb-3">
                    <h6>Property Unit</h6>
                    <p className="mb-1"><strong>{getSelectedProperty().unitNumber}</strong></p>
                    <p className="mb-1 text-muted">{getSelectedProperty().unitType}</p>
                    <p className="mb-0 text-muted">
                      {getSelectedProperty().bedrooms} bed, {getSelectedProperty().bathrooms} bath
                    </p>
                  </div>
                )}

                {formData.contractStartDate && formData.contractEndDate && (
                  <div className="mb-3">
                    <h6>Contract Period</h6>
                    <p className="mb-1">
                      <strong>From:</strong> {new Date(formData.contractStartDate).toLocaleDateString()}
                    </p>
                    <p className="mb-0">
                      <strong>To:</strong> {new Date(formData.contractEndDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Amount Breakdown */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faCalculator} className="text-primary me-2" />
                  Amount Breakdown
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span>Total Rental Amount:</span>
                    <strong>{formatCurrency(calculatedAmounts.totalRentalAmount, calculatedAmounts.currency)}</strong>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span>Number of Cheques:</span>
                    <strong>{calculatedAmounts.numberOfCheques}</strong>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span>Cheque Amount:</span>
                    <strong>{formatCurrency(calculatedAmounts.chequeAmount, calculatedAmounts.currency)}</strong>
                  </div>
                </div>
                <hr />
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span>Admin Fees ({calculatedAmounts.brokeragePercentage}%):</span>
                    <strong>{formatCurrency(calculatedAmounts.brokerageFee, calculatedAmounts.currency)}</strong>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <span className="fw-bold">Total Amount:</span>
                    <strong className="text-primary">{formatCurrency(calculatedAmounts.totalAmount, calculatedAmounts.currency)}</strong>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Cheque Schedule */}
            {calculatedAmounts.numberOfCheques > 1 && (
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-transparent border-0">
                  <h5 className="mb-0">Cheque Schedule</h5>
                </Card.Header>
                <Card.Body>
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Cheque #</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: calculatedAmounts.numberOfCheques }, (_, i) => (
                        <tr key={i + 1}>
                          <td>{i + 1}</td>
                          <td>{formatCurrency(calculatedAmounts.chequeAmount, calculatedAmounts.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* Submit Button */}
        <div className="d-flex justify-content-center mt-4">
          <Button 
            type="submit" 
            variant="primary" 
            size="lg"
            disabled={saving}
          >
            <FontAwesomeIcon icon={faSave} className="me-2" />
            {saving ? 'Creating Contract...' : 'Create Contract'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateContract; 